/**
 * Batch 27h — Edit-tenant dialog (name only).
 * B-19 extension — adds max_users (anti-abuse seat cap) editable field.
 *
 * Spec §1.3: ``name`` is the only editable field in 27h. Slug, legal capture,
 * Stripe IDs, plan, status are all locked.
 *
 * B-19: ``max_users`` is SA-editable. Display hint: "Max users: N (default: 100, demo default: 5)".
 */
import { useEffect, useState } from "react";
import {
  useUpdateTenant,
  type TenantDetail,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenant: TenantDetail;
  onClose: () => void;
}

export function EditTenantDialog({ tenant, onClose }: Props): JSX.Element {
  const [name, setName] = useState(tenant.name);
  const [maxUsers, setMaxUsers] = useState(String(tenant.max_users));
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const update = useUpdateTenant(tenant.id);
  const addToast = useToastStore((s) => s.addToast);

  useEffect(() => {
    setName(tenant.name);
    setMaxUsers(String(tenant.max_users));
    setErrMsg(null);
  }, [tenant.id, tenant.name, tenant.max_users]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setErrMsg("Name is required.");
      return;
    }

    const parsedMaxUsers = parseInt(maxUsers, 10);
    if (!Number.isInteger(parsedMaxUsers) || parsedMaxUsers < 1) {
      setErrMsg("Max users must be a whole number ≥ 1.");
      return;
    }

    const nameChanged = trimmedName !== tenant.name;
    const maxUsersChanged = parsedMaxUsers !== tenant.max_users;

    if (!nameChanged && !maxUsersChanged) {
      onClose();
      return;
    }

    const body: { name?: string; max_users?: number } = {};
    if (nameChanged) body.name = trimmedName;
    if (maxUsersChanged) body.max_users = parsedMaxUsers;

    update.mutate(body, {
      onSuccess: () => {
        addToast("Tenant updated.", "success");
        onClose();
      },
      onError: (err) => {
        setErrMsg(err.message || "Could not update tenant.");
      },
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
          Edit tenant
        </h2>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Tenant name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Max users{" "}
            <span className="text-xs text-accent-primary dark:text-brand-100/40">
              (default: 100, demo default: 5)
            </span>
          </span>
          <input
            type="number"
            value={maxUsers}
            onChange={(e) => setMaxUsers(e.target.value)}
            required
            min={1}
            step={1}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
          />
          <span className="mt-0.5 block text-xs text-accent-primary dark:text-brand-100/40">
            Anti-abuse guard — not pricing. Cannot be set below current active user count.
          </span>
        </label>
        <p className="text-xs text-accent-primary dark:text-brand-100/40">
          Slug, legal capture, and billing IDs are immutable. Use the suspend
          / reinstate actions to change status; use the Stripe surface to
          change plan.
        </p>
        {errMsg ? (
          <p className="text-sm text-red-600 dark:text-red-400">{errMsg}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
